import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { FaPlus, FaEdit, FaTrash, FaUser, FaShieldAlt, FaCheck, FaTimes, FaKey, FaLock, FaEye, FaEyeSlash, FaSignOutAlt, FaQrcode, FaUnlockAlt } from 'react-icons/fa';
import { ALL_PERMISSIONS, getUser } from '../utils/authHelper';

const P = {
  bg: '#0d0e10', card: 'rgba(255,255,255,.04)', border: 'rgba(255,255,255,.07)',
  text: '#f0eeff', sub: 'rgba(240,238,255,.45)', faint: 'rgba(240,238,255,.2)',
  accent: '#f27127', purple: '#a78bfa',
};

const GROUPED = ALL_PERMISSIONS.reduce((acc, p) => {
  if (!acc[p.group]) acc[p.group] = [];
  acc[p.group].push(p);
  return acc;
}, {});

const GROUP_COLORS = {
  'Genel': '#a78bfa', 'Bonus Talepleri': '#60a5fa', 'Ayarlar': '#f27127',
  'Kullanıcı': '#f87171', 'Finansal': '#f59e0b', 'Yönetim': '#34d399',
};

const BOSH_FORM  = { username: '', password: '', displayName: '', title: '', permissions: [], isActive: true };
const BOSH_PW    = { newPassword: '', confirmPassword: '' };
const ALL_PERM_KEYS = ALL_PERMISSIONS.map(p => p.key);
const hasAllPerms   = (u) => !u.isSuperAdmin && ALL_PERM_KEYS.every(k => (u.permissions || []).includes(k));

/* ── Şifre input — göster/gizle butonlu ── */
const PwInput = ({ value, onChange, placeholder, autoFocus, onKeyDown }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <FaKey style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,.2)', fontSize: '.75rem', zIndex: 1 }} />
      <input
        type={show ? 'text' : 'password'} value={value} onChange={onChange}
        placeholder={placeholder} autoFocus={autoFocus} onKeyDown={onKeyDown}
        style={{ width: '100%', padding: '10px 38px 10px 34px', borderRadius: 10, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', color: P.text, fontSize: '.82rem', fontFamily: "'Outfit',sans-serif", outline: 'none', boxSizing: 'border-box' }}
      />
      <button type="button" onClick={() => setShow(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: show ? P.purple : 'rgba(255,255,255,.25)', fontSize: '.8rem', padding: 2, display: 'flex', alignItems: 'center' }}>
        {show ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
  );
};

/* ── Overlay ── */
const Overlay = ({ onClick, children, zIndex = 1000 }) => (
  <div onClick={onClick} style={{ position: 'fixed', inset: 0, zIndex, background: 'rgba(0,0,0,.78)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
    {children}
  </div>
);

export default function UserManagement() {
  const me = getUser();

  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null);
  const [form,       setForm]       = useState(BOSH_FORM);
  const [editId,     setEditId]     = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [deleteConf, setDeleteConf] = useState(null);
  const [termConf,   setTermConf]   = useState(false);
  const [pwModal,    setPwModal]    = useState(null);
  const [pwForm,     setPwForm]     = useState(BOSH_PW);
  const [pwSaving,   setPwSaving]   = useState(false);
  const [err,        setErr]        = useState('');
  const [pwErr,      setPwErr]      = useState('');
  const [success,    setSuccess]    = useState('');

  const [twoFaConf,   setTwoFaConf]   = useState(null); // { id, name }
  const [req2faConf,  setReq2faConf]  = useState(null); // { id, name, current }

  const notify = (msg, isErr = false) => {
    if (isErr) { setErr(msg); setTimeout(() => setErr(''), 4000); }
    else { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try { const r = await axiosInstance.get('/auth/users'); setUsers(r.data); }
    catch { notify('Kullanıcılar alınamadı.', true); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const togglePerm = (key) => setForm(f => ({
    ...f, permissions: f.permissions.includes(key) ? f.permissions.filter(p => p !== key) : [...f.permissions, key],
  }));

  const toggleGroup = (group) => {
    const keys = GROUPED[group].map(p => p.key);
    const allOn = keys.every(k => form.permissions.includes(k));
    setForm(f => ({ ...f, permissions: allOn ? f.permissions.filter(k => !keys.includes(k)) : [...new Set([...f.permissions, ...keys])] }));
  };

  const openCreate = () => { setForm(BOSH_FORM); setEditId(null); setModal('create'); setErr(''); };
  const openEdit   = (u) => { setForm({ username: u.username, password: '', displayName: u.displayName || '', title: u.title || '', permissions: u.permissions || [], isActive: u.isActive }); setEditId(u._id); setModal('edit'); setErr(''); };
  const openPw     = (u) => { setPwModal({ id: u._id, name: u.displayName || u.username }); setPwForm(BOSH_PW); setPwErr(''); };

  const handleSave = async () => {
    setErr('');
    if (!form.username.trim()) return notify('Kullanıcı adı gerekli.', true);
    if (modal === 'create' && !form.password.trim()) return notify('Şifre gerekli.', true);
    if (modal === 'create' && form.password.length < 6) return notify('Şifre en az 6 karakter olmalı.', true);
    setSaving(true);
    try {
      const payload = { username: form.username.trim(), displayName: form.displayName.trim() || form.username.trim(), title: form.title.trim(), permissions: form.permissions, isActive: form.isActive, ...(modal === 'create' ? { password: form.password } : {}) };
      if (modal === 'create') { await axiosInstance.post('/auth/users', payload); notify('Kullanıcı oluşturuldu ✓'); }
      else { await axiosInstance.put(`/auth/users/${editId}`, payload); notify('Kullanıcı güncellendi ✓'); }
      setModal(null); fetchUsers();
    } catch (e) { notify(e.response?.data?.message || 'Bir hata oluştu.', true); }
    finally { setSaving(false); }
  };

  const handlePwSave = async () => {
    setPwErr('');
    if (!pwForm.newPassword) return setPwErr('Yeni şifre boş olamaz.');
    if (pwForm.newPassword.length < 6) return setPwErr('Şifre en az 6 karakter olmalı.');
    if (pwForm.newPassword !== pwForm.confirmPassword) return setPwErr('Şifreler eşleşmiyor.');
    setPwSaving(true);
    try { await axiosInstance.put(`/auth/users/${pwModal.id}`, { password: pwForm.newPassword }); notify('Şifre güncellendi ✓'); setPwModal(null); }
    catch (e) { setPwErr(e.response?.data?.message || 'Şifre güncellenemedi.'); }
    finally { setPwSaving(false); }
  };

  const handleDelete = async () => {
    try { await axiosInstance.delete(`/auth/users/${deleteConf.id}`); notify('Kullanıcı silindi.'); setDeleteConf(null); fetchUsers(); }
    catch (e) { notify(e.response?.data?.message || 'Silinemedi.', true); }
  };

  const terminateAll = async () => {
    try { const r = await axiosInstance.post('/auth/sessions/terminate-all'); notify(r.data.message || 'Tüm oturumlar sonlandırıldı.'); setTermConf(false); fetchUsers(); }
    catch (e) { notify(e.response?.data?.message || 'İşlem başarısız.', true); }
  };

  const logoutUser = async (u) => {
    try { await axiosInstance.post(`/auth/users/${u._id}/logout`); notify(`${u.displayName || u.username} oturumu kapatıldı.`); }
    catch (e) { notify(e.response?.data?.message || 'Oturum kapatılamadı.', true); }
  };

  const toggleActive = async (u) => {
    try { await axiosInstance.put(`/auth/users/${u._id}`, { isActive: !u.isActive }); fetchUsers(); }
    catch { notify('Güncellenemedi.', true); }
  };

  const reset2FA = async () => {
    try {
      await axiosInstance.post('/auth/2fa/disable', { userId: twoFaConf.id });
      notify(`${twoFaConf.name} için 2FA sıfırlandı.`);
      setTwoFaConf(null);
      fetchUsers();
    } catch (e) { notify(e.response?.data?.message || '2FA sıfırlanamadı.', true); }
  };

  const toggle2FARequired = async () => {
    try {
      await axiosInstance.put(`/auth/users/${req2faConf.id}`, { require2FA: !req2faConf.current });
      notify(`${req2faConf.name} için 2FA zorunluluğu ${!req2faConf.current ? 'açıldı' : 'kapatıldı'}.`);
      setReq2faConf(null);
      fetchUsers();
    } catch (e) { notify(e.response?.data?.message || 'Güncellenemedi.', true); }
  };

  const lbl = { fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: P.faint, display: 'block', marginBottom: 5 };
  const inp = { width: '100%', padding: '10px 13px', borderRadius: 10, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', color: P.text, fontSize: '.82rem', fontFamily: "'Outfit',sans-serif", outline: 'none', boxSizing: 'border-box' };
  const iconBtn = (bg, color) => ({ width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.75rem' });

  /* Kullanıcı tipi hesapla */
  const userType = (u) => {
    if (u.isSuperAdmin) return 'super';
    if (hasAllPerms(u)) return 'full';
    return 'normal';
  };

  const typeColors = {
    super:  { bg: 'linear-gradient(135deg,rgba(167,139,250,.3),rgba(167,139,250,.08))', border: 'rgba(167,139,250,.3)', color: P.purple },
    full:   { bg: 'linear-gradient(135deg,rgba(167,139,250,.2),rgba(167,139,250,.05))', border: 'rgba(167,139,250,.2)', color: P.purple },
    normal: { bg: 'linear-gradient(135deg,rgba(242,113,39,.3),rgba(242,113,39,.08))',  border: 'rgba(242,113,39,.25)', color: P.accent  },
  };

  return (
    <>
      {/* ── ANA SAYFA ── */}
      <div style={{ minHeight: '100vh', background: P.bg, padding: '28px', fontFamily: "'Outfit',sans-serif", color: P.text }}>

        {/* Toast */}
        {(err || success) && (
          <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '13px 18px', borderRadius: 12, fontSize: '.82rem', fontWeight: 600, background: err ? 'rgba(239,68,68,.15)' : 'rgba(16,185,129,.13)', border: `1px solid ${err ? 'rgba(239,68,68,.3)' : 'rgba(16,185,129,.3)'}`, color: err ? '#f87171' : '#34d399', boxShadow: '0 8px 30px rgba(0,0,0,.5)' }}>
            {err || success}
          </div>
        )}

        {/* Başlık */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-.4px', margin: 0, color: P.text }}>Kullanıcı Yönetimi</h1>
            <p style={{ fontSize: '.76rem', color: P.faint, marginTop: 4 }}>Panel kullanıcılarını ve erişim izinlerini yönetin.</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setTermConf(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 11, border: '1px solid rgba(239,68,68,.25)', cursor: 'pointer', background: 'rgba(239,68,68,.08)', color: '#f87171', fontSize: '.82rem', fontWeight: 700, fontFamily: "'Outfit',sans-serif" }}>
              <FaSignOutAlt style={{ fontSize: '.7rem' }} /> Tüm Oturumları Sonlandır
            </button>
            <button onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', borderRadius: 11, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#f27127,#c44b0a)', color: '#fff', fontSize: '.82rem', fontWeight: 700, fontFamily: "'Outfit',sans-serif", boxShadow: '0 4px 16px rgba(242,113,39,.3)' }}>
              <FaPlus style={{ fontSize: '.7rem' }} /> Yeni Kullanıcı
            </button>
          </div>
        </div>

        {/* Liste */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} style={{ height: 72, borderRadius: 14, background: P.card, border: `1px solid ${P.border}` }} />
            ))
          ) : users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: P.faint }}>
              <FaUser style={{ fontSize: '2rem', opacity: .2, display: 'block', margin: '0 auto 10px' }} />
              Kullanıcı bulunamadı.
            </div>
          ) : users.map(u => {
            const type = userType(u);
            const tc   = typeColors[type];
            return (
              <div key={u._id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderRadius: 14, background: P.card, border: `1px solid ${P.border}`, opacity: u.isActive ? 1 : .55, transition: 'opacity .2s' }}>

                {/* Avatar */}
                <div style={{ width: 40, height: 40, borderRadius: 11, flexShrink: 0, background: tc.bg, border: `1px solid ${tc.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tc.color, fontWeight: 700, fontSize: '.72rem' }}>
                  {type !== 'normal' ? <FaShieldAlt /> : (u.displayName || u.username || '?')[0].toUpperCase()}
                </div>

                {/* Bilgi */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '.88rem', color: P.text }}>{u.displayName || u.username}</span>
                    {u.displayName && u.displayName !== u.username && <span style={{ fontSize: '.65rem', color: P.faint }}>@{u.username}</span>}
                    {u.title && <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', color: P.sub, fontSize: '.62rem', fontWeight: 600 }}>{u.title}</span>}
                    {u.isSuperAdmin && <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(167,139,250,.12)', border: '1px solid rgba(167,139,250,.25)', color: P.purple, fontSize: '.62rem', fontWeight: 700 }}>👑 Super Admin</span>}
                    {!u.isSuperAdmin && hasAllPerms(u) && <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(167,139,250,.12)', border: '1px solid rgba(167,139,250,.25)', color: P.purple, fontSize: '.62rem', fontWeight: 700 }}>⚡ Tam Yetkili</span>}
                    {!u.isActive && <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.2)', color: '#f87171', fontSize: '.62rem', fontWeight: 700 }}>Pasif</span>}
                    {u.twoFactorEnabled
                      ? <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(16,185,129,.1)', border: '1px solid rgba(16,185,129,.25)', color: '#10b981', fontSize: '.62rem', fontWeight: 700 }}>🔐 2FA Aktif</span>
                      : <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)', color: '#f87171', fontSize: '.62rem', fontWeight: 700 }}>⚠ 2FA Yok</span>
                    }
                    {!u.isSuperAdmin && (
                      u.require2FA !== false
                        ? <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(251,191,36,.07)', border: '1px solid rgba(251,191,36,.2)', color: '#fbbf24', fontSize: '.62rem', fontWeight: 700 }}>🔒 2FA Zorunlu</span>
                        : <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(148,163,184,.07)', border: '1px solid rgba(148,163,184,.15)', color: '#94a3b8', fontSize: '.62rem', fontWeight: 700 }}>🔓 2FA İsteğe Bağlı</span>
                    )}
                  </div>
                  {type !== 'normal' ? (
                    <span style={{ fontSize: '.65rem', color: 'rgba(167,139,250,.5)' }}>Tüm sayfalara erişim</span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(u.permissions || []).length === 0
                        ? <span style={{ fontSize: '.62rem', color: 'rgba(239,68,68,.5)' }}>İzin yok</span>
                        : (u.permissions || []).slice(0, 8).map(p => {
                            const meta = ALL_PERMISSIONS.find(x => x.key === p);
                            const col  = GROUP_COLORS[meta?.group] || '#94a3b8';
                            return <span key={p} style={{ padding: '2px 7px', borderRadius: 5, fontSize: '.58rem', fontWeight: 600, background: `${col}14`, border: `1px solid ${col}28`, color: col }}>{meta?.label || p}</span>;
                          })
                      }
                      {(u.permissions || []).length > 8 && <span style={{ fontSize: '.62rem', color: P.faint }}>+{u.permissions.length - 8} daha</span>}
                    </div>
                  )}
                </div>

                {/* Son giriş + aktif oturum */}
                <div style={{ fontSize: '.62rem', color: P.faint, flexShrink: 0, textAlign: 'right' }}>
                  {u.lastLogin && (
                    <>
                      <div>Son giriş</div>
                      <div style={{ color: P.sub }}>{new Date(u.lastLogin).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                    </>
                  )}
                  <div style={{ marginTop: u.lastLogin ? 4 : 0, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: (u.sessionCount || 0) > 0 ? '#10b981' : 'rgba(255,255,255,.15)', display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: (u.sessionCount || 0) > 0 ? '#10b981' : P.faint }}>
                      {u.sessionCount || 0} aktif oturum
                    </span>
                  </div>
                </div>

                {/* Aksiyonlar */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {!u.isSuperAdmin && (
                    <button onClick={() => toggleActive(u)} title={u.isActive ? 'Pasife al' : 'Aktife al'} style={iconBtn(u.isActive ? 'rgba(16,185,129,.12)' : 'rgba(239,68,68,.1)', u.isActive ? '#10b981' : '#f87171')}>
                      {u.isActive ? <FaCheck /> : <FaTimes />}
                    </button>
                  )}
                  {(!u.isSuperAdmin || u._id === me?.id) && (
                    <button onClick={() => openEdit(u)} title="Düzenle" style={iconBtn('rgba(99,102,241,.12)', '#818cf8')}>
                      <FaEdit />
                    </button>
                  )}
                  {(!u.isSuperAdmin || u._id === me?.id) && (
                    <button onClick={() => openPw(u)} title="Şifre Değiştir" style={iconBtn('rgba(167,139,250,.1)', P.purple)}>
                      <FaKey />
                    </button>
                  )}
                  {u._id !== me?.id && (
                    <button onClick={() => logoutUser(u)} title={`Tüm Oturumları Kapat${u.sessionCount > 0 ? ` (${u.sessionCount} aktif)` : ''}`} style={{ ...iconBtn('rgba(251,191,36,.08)', '#fbbf24'), position: 'relative' }}>
                      <FaSignOutAlt />
                      {u.sessionCount > 0 && (
                        <span style={{ position: 'absolute', top: -4, right: -4, width: 14, height: 14, borderRadius: '50%', background: '#fbbf24', color: '#000', fontSize: '.5rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                          {u.sessionCount > 9 ? '9+' : u.sessionCount}
                        </span>
                      )}
                    </button>
                  )}
                  {!u.isSuperAdmin && u.twoFactorEnabled && (
                    <button onClick={() => setTwoFaConf({ id: u._id, name: u.displayName || u.username })} title="2FA Sıfırla" style={iconBtn('rgba(16,185,129,.08)', '#10b981')}>
                      <FaUnlockAlt />
                    </button>
                  )}
                  {!u.isSuperAdmin && (
                    <button
                      onClick={() => setReq2faConf({ id: u._id, name: u.displayName || u.username, current: u.require2FA !== false })}
                      title={u.require2FA !== false ? '2FA Zorunluluğunu Kaldır' : '2FA Zorunlu Yap'}
                      style={iconBtn(u.require2FA !== false ? 'rgba(251,191,36,.1)' : 'rgba(148,163,184,.08)', u.require2FA !== false ? '#fbbf24' : '#94a3b8')}
                    >
                      <FaShieldAlt />
                    </button>
                  )}
                  {!u.isSuperAdmin && (
                    <button onClick={() => setDeleteConf({ id: u._id, name: u.displayName || u.username })} title="Sil" style={iconBtn('rgba(239,68,68,.1)', '#f87171')}>
                      <FaTrash />
                    </button>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      </div>

      {/* ── CREATE / EDIT MODAL ── */}
      {modal && (
        <Overlay onClick={() => setModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 620, background: '#131028', borderRadius: 20, border: '1px solid rgba(167,139,250,.15)', boxShadow: '0 24px 60px rgba(0,0,0,.7)', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>

            {/* Başlık */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(242,113,39,.15)', border: '1px solid rgba(242,113,39,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.accent }}>
                {modal === 'create' ? <FaPlus /> : <FaEdit />}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '.95rem', color: P.text }}>{modal === 'create' ? 'Yeni Kullanıcı' : 'Kullanıcı Düzenle'}</div>
                <div style={{ fontSize: '.65rem', color: P.faint, marginTop: 1 }}>İzinleri tek tek seçin</div>
              </div>
            </div>

            {/* Form scroll alanı */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
              {err && <div style={{ padding: '10px 13px', borderRadius: 9, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.2)', color: '#f87171', fontSize: '.78rem', marginBottom: 16 }}>{err}</div>}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={lbl}>Kullanıcı Adı *</label>
                  <input style={inp} value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="ornek_kullanici" disabled={modal === 'edit'} />
                </div>
                <div>
                  <label style={lbl}>Görünen Ad</label>
                  <input style={inp} value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} placeholder="Ahmet Yılmaz" />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Ünvan</label>
                <input style={inp} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Operatör, Muhasebe, Kasa Sorumlusu..." />
              </div>

              {modal === 'create' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Şifre *</label>
                  <PwInput value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="En az 6 karakter" />
                </div>
              )}

              {/* Aktif toggle — superadmin için gösterme */}
              {!users.find(u => u._id === editId)?.isSuperAdmin ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)', marginBottom: 20 }}>
                  <span style={{ fontSize: '.8rem', fontWeight: 600, color: P.text }}>Hesap Durumu</span>
                  <div onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none', padding: '5px 12px', borderRadius: 8, background: form.isActive ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.08)', border: `1px solid ${form.isActive ? 'rgba(16,185,129,.25)' : 'rgba(239,68,68,.2)'}`, color: form.isActive ? '#10b981' : '#f87171', fontSize: '.78rem', fontWeight: 700 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: form.isActive ? '#10b981' : '#f87171', display: 'inline-block' }} />
                    {form.isActive ? 'Aktif' : 'Pasif'}
                  </div>
                </div>
              ) : <div style={{ marginBottom: 20 }} />}

              {/* İzinler — superadmin için gösterme */}
              {!users.find(u => u._id === editId)?.isSuperAdmin && (
                <>
                  <div style={{ fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: P.faint, marginBottom: 12 }}>Sayfa İzinleri</div>
                  {Object.entries(GROUPED).map(([group, perms]) => {
                    const col   = GROUP_COLORS[group] || '#94a3b8';
                    const allOn = perms.every(p => form.permissions.includes(p.key));
                    return (
                      <div key={group} style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                          <span style={{ fontSize: '.68rem', fontWeight: 700, color: col }}>{group}</span>
                          <button onClick={() => toggleGroup(group)} style={{ padding: '2px 10px', borderRadius: 6, border: `1px solid ${col}30`, background: allOn ? `${col}18` : 'transparent', color: allOn ? col : 'rgba(255,255,255,.25)', fontSize: '.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>
                            {allOn ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {perms.map(p => {
                            const on = form.permissions.includes(p.key);
                            return (
                              <div key={p.key} onClick={() => togglePerm(p.key)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderRadius: 9, cursor: 'pointer', userSelect: 'none', background: on ? `${col}14` : 'rgba(255,255,255,.03)', border: `1px solid ${on ? col + '35' : 'rgba(255,255,255,.07)'}`, transition: 'all .15s' }}>
                                <div style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, background: on ? col : 'transparent', border: `2px solid ${on ? col : 'rgba(255,255,255,.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {on && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                </div>
                                <span style={{ fontSize: '.73rem', fontWeight: on ? 600 : 400, color: on ? P.text : P.faint }}>{p.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Alt butonlar */}
            <div style={{ padding: '14px 24px 20px', borderTop: '1px solid rgba(255,255,255,.05)', display: 'flex', gap: 10 }}>
              <button onClick={() => setModal(null)} style={{ flex: 1, padding: '11px', borderRadius: 11, border: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.04)', color: 'rgba(255,255,255,.4)', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>İptal</button>
              <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '11px', borderRadius: 11, border: 'none', cursor: saving ? 'not-allowed' : 'pointer', background: saving ? 'rgba(242,113,39,.4)' : 'linear-gradient(135deg,#f27127,#c44b0a)', color: '#fff', fontSize: '.82rem', fontWeight: 700, fontFamily: "'Outfit',sans-serif", boxShadow: saving ? 'none' : '0 4px 14px rgba(242,113,39,.25)' }}>
                {saving ? 'Kaydediliyor...' : modal === 'create' ? 'Kullanıcı Oluştur' : 'Değişiklikleri Kaydet'}
              </button>
            </div>

          </div>
        </Overlay>
      )}

      {/* ── ŞİFRE DEĞİŞTİR MODAL ── */}
      {pwModal && (
        <Overlay onClick={() => setPwModal(null)} zIndex={1001}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 440, background: '#131028', borderRadius: 20, border: '1px solid rgba(167,139,250,.2)', boxShadow: '0 24px 60px rgba(0,0,0,.8)', overflow: 'hidden' }}>

            <div style={{ padding: '22px 24px 18px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(167,139,250,.15)', border: '1px solid rgba(167,139,250,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.purple, fontSize: '1rem' }}>
                <FaLock />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '.95rem', color: P.text }}>Şifre Değiştir</div>
                <div style={{ fontSize: '.65rem', color: P.faint, marginTop: 2 }}>{pwModal.name}</div>
              </div>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {pwErr && <div style={{ padding: '10px 13px', borderRadius: 9, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.2)', color: '#f87171', fontSize: '.78rem', marginBottom: 16 }}>{pwErr}</div>}
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Yeni Şifre</label>
                <PwInput value={pwForm.newPassword} onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="En az 6 karakter" autoFocus />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={lbl}>Şifre Tekrar</label>
                <PwInput value={pwForm.confirmPassword} onChange={e => setPwForm(f => ({ ...f, confirmPassword: e.target.value }))} placeholder="Şifreyi tekrar girin" onKeyDown={e => e.key === 'Enter' && handlePwSave()} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setPwModal(null)} style={{ flex: 1, padding: '11px', borderRadius: 11, border: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.04)', color: 'rgba(255,255,255,.4)', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>İptal</button>
                <button onClick={handlePwSave} disabled={pwSaving} style={{ flex: 2, padding: '11px', borderRadius: 11, border: 'none', cursor: pwSaving ? 'not-allowed' : 'pointer', background: pwSaving ? 'rgba(167,139,250,.3)' : 'linear-gradient(135deg,#a78bfa,#7c3aed)', color: '#fff', fontSize: '.82rem', fontWeight: 700, fontFamily: "'Outfit',sans-serif", boxShadow: pwSaving ? 'none' : '0 4px 14px rgba(167,139,250,.25)' }}>
                  {pwSaving ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
                </button>
              </div>
            </div>

          </div>
        </Overlay>
      )}

      {/* ── SİLME ONAY ── */}
      {deleteConf && (
        <Overlay onClick={() => setDeleteConf(null)} zIndex={1002}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 400, background: '#131028', borderRadius: 20, border: '1px solid rgba(239,68,68,.2)', boxShadow: '0 24px 60px rgba(0,0,0,.8)', overflow: 'hidden' }}>
            <div style={{ padding: '32px 28px 24px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px', background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171', fontSize: '1.4rem' }}>
                <FaTrash />
              </div>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: P.text, marginBottom: 6 }}>Kullanıcıyı Sil</div>
              <div style={{ fontSize: '.8rem', color: P.faint, lineHeight: 1.6 }}>
                <span style={{ color: '#f87171', fontWeight: 700 }}>{deleteConf.name}</span> adlı kullanıcı kalıcı olarak silinecek. Bu işlem geri alınamaz.
              </div>
            </div>
            <div style={{ padding: '16px 24px 20px', display: 'flex', gap: 10 }}>
              <button onClick={() => setDeleteConf(null)} style={{ flex: 1, padding: '12px', borderRadius: 11, border: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.04)', color: P.sub, fontSize: '.84rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>Vazgeç</button>
              <button onClick={handleDelete} style={{ flex: 1.5, padding: '12px', borderRadius: 11, border: 'none', background: 'linear-gradient(135deg,#ef4444,#b91c1c)', color: '#fff', fontSize: '.84rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Outfit',sans-serif", boxShadow: '0 4px 16px rgba(239,68,68,.3)' }}>Evet, Sil</button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── 2FA SIFIRLAMA ONAY ── */}
      {twoFaConf && (
        <Overlay onClick={() => setTwoFaConf(null)} zIndex={1002}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 400, background: '#131028', borderRadius: 20, border: '1px solid rgba(16,185,129,.2)', boxShadow: '0 24px 60px rgba(0,0,0,.8)', overflow: 'hidden' }}>
            <div style={{ padding: '32px 28px 24px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px', background: 'rgba(16,185,129,.1)', border: '1px solid rgba(16,185,129,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981', fontSize: '1.4rem' }}>
                <FaUnlockAlt />
              </div>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: P.text, marginBottom: 6 }}>2FA Sıfırla</div>
              <div style={{ fontSize: '.8rem', color: P.faint, lineHeight: 1.6 }}>
                <span style={{ color: '#10b981', fontWeight: 700 }}>{twoFaConf.name}</span> adlı kullanıcının 2FA ayarı sıfırlanacak. Bir sonraki girişte yeniden kurulum yapması gerekecek.
              </div>
            </div>
            <div style={{ padding: '16px 24px 20px', display: 'flex', gap: 10 }}>
              <button onClick={() => setTwoFaConf(null)} style={{ flex: 1, padding: '12px', borderRadius: 11, border: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.04)', color: P.sub, fontSize: '.84rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>Vazgeç</button>
              <button onClick={reset2FA} style={{ flex: 1.5, padding: '12px', borderRadius: 11, border: 'none', background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', fontSize: '.84rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Outfit',sans-serif", boxShadow: '0 4px 16px rgba(16,185,129,.25)' }}>Evet, Sıfırla</button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── 2FA ZORUNLULUK TOGGLE ONAY ── */}
      {req2faConf && (
        <Overlay onClick={() => setReq2faConf(null)} zIndex={1002}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 400, background: '#131028', borderRadius: 20, border: `1px solid ${req2faConf.current ? 'rgba(148,163,184,.2)' : 'rgba(251,191,36,.2)'}`, boxShadow: '0 24px 60px rgba(0,0,0,.8)', overflow: 'hidden' }}>
            <div style={{ padding: '32px 28px 24px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px', background: req2faConf.current ? 'rgba(148,163,184,.1)' : 'rgba(251,191,36,.1)', border: `1px solid ${req2faConf.current ? 'rgba(148,163,184,.25)' : 'rgba(251,191,36,.25)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: req2faConf.current ? '#94a3b8' : '#fbbf24', fontSize: '1.4rem' }}>
                <FaShieldAlt />
              </div>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: P.text, marginBottom: 6 }}>
                {req2faConf.current ? '2FA Zorunluluğunu Kaldır' : '2FA Zorunlu Yap'}
              </div>
              <div style={{ fontSize: '.8rem', color: P.faint, lineHeight: 1.6 }}>
                <span style={{ color: req2faConf.current ? '#94a3b8' : '#fbbf24', fontWeight: 700 }}>{req2faConf.name}</span>{' '}
                {req2faConf.current
                  ? 'için 2FA zorunluluğu kaldırılacak. Bu kullanıcı 2FA olmadan giriş yapabilecek.'
                  : 'için 2FA zorunlu hale getirilecek. Bir sonraki girişte kurulum yapması gerekecek.'}
              </div>
            </div>
            <div style={{ padding: '16px 24px 20px', display: 'flex', gap: 10 }}>
              <button onClick={() => setReq2faConf(null)} style={{ flex: 1, padding: '12px', borderRadius: 11, border: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.04)', color: P.sub, fontSize: '.84rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>Vazgeç</button>
              <button onClick={toggle2FARequired} style={{ flex: 1.5, padding: '12px', borderRadius: 11, border: 'none', background: req2faConf.current ? 'linear-gradient(135deg,#64748b,#475569)' : 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#fff', fontSize: '.84rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Outfit',sans-serif", boxShadow: req2faConf.current ? 'none' : '0 4px 16px rgba(251,191,36,.25)' }}>
                {req2faConf.current ? 'Evet, Kaldır' : 'Evet, Zorunlu Yap'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── TÜM OTURUMLARI SONLANDIR ── */}}
      {termConf && (
        <Overlay onClick={() => setTermConf(false)} zIndex={1003}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, background: '#131028', borderRadius: 20, border: '1px solid rgba(251,191,36,.2)', boxShadow: '0 24px 60px rgba(0,0,0,.8)', overflow: 'hidden' }}>
            <div style={{ padding: '32px 28px 24px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px', background: 'rgba(251,191,36,.1)', border: '1px solid rgba(251,191,36,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fbbf24', fontSize: '1.4rem' }}>
                <FaSignOutAlt />
              </div>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: P.text, marginBottom: 8 }}>Tüm Oturumları Sonlandır</div>
              <div style={{ fontSize: '.8rem', color: P.faint, lineHeight: 1.7 }}>
                Super Admin hariç <span style={{ color: '#fbbf24', fontWeight: 700 }}>tüm kullanıcıların</span> aktif oturumları kapatılacak. Kullanıcılar bir sonraki işlemlerinde otomatik olarak çıkış yapmış olacak.
              </div>
            </div>
            <div style={{ padding: '16px 24px 20px', display: 'flex', gap: 10 }}>
              <button onClick={() => setTermConf(false)} style={{ flex: 1, padding: '12px', borderRadius: 11, border: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.04)', color: P.sub, fontSize: '.84rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>Vazgeç</button>
              <button onClick={terminateAll} style={{ flex: 1.5, padding: '12px', borderRadius: 11, border: 'none', background: 'linear-gradient(135deg,#f59e0b,#b45309)', color: '#fff', fontSize: '.84rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Outfit',sans-serif", boxShadow: '0 4px 16px rgba(251,191,36,.25)' }}>Evet, Sonlandır</button>
            </div>
          </div>
        </Overlay>
      )}
    </>
  );
}
